// CreativeEditor — the ONE editor (formerly the v4 prototype). A full-screen,
// Figma/Canva-style editor that REUSES the proven primitives — composeRender
// (drag math, layer style, flatten, stacking), MarqueeOverlay, gallery/api lib —
// in a 100vh, no-page-scroll shell:
//
//   ┌──────────────── top bar (back · title · version · Delete) ───────────────┐
//   │  [layers rail] │  centered auto-fit canvas + floating AI bar │ [props]  │
//   └────────────────┴─────────────────────────────────────────────┴─────────┘
//
// Data model = Model A (overlay_json + logo_overlay_json). It OWNS its own
// `postId` state so version-switch + save-fork happen in place (no route change),
// which lets it be embedded (wizard finish step) OR routed (/editor-v4/:id).
// All navigation is parameterized via props:
//   - onBack:            the top-left back button (route → /gallery; embed →
//                        "Back to design", returns to the template picker).
//   - onActivePostChange: optional URL sync when the active post forks/switches.
//   - onDeleted:         after a delete (route → /gallery).
//   - showDelete/embedded: header + shell chrome toggles.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MarqueeOverlay from '@/components/feature/MarqueeOverlay';
import {
  getPost,
  saveCreativeVersion,
  marqueeEditPreview,
  listCreativeVersions,
  deletePost,
  prepareTextEdit,
  type Post,
} from '@/lib/gallery';
import { wizardEditImage, getBrand, suggestCopy } from '@/lib/api';
import {
  aspectClass,
  aspectToRefHeight,
  clampAnchor,
  flattenComposedFrame,
  getTextLayerStyle,
  resolveStackedLayers,
  snapForPosition,
  REFERENCE_CANVAS_WIDTH,
  OVERLAY_PAD_FRAC,
  type LogoOverlay,
  type LogoTint,
  type TextOverlay,
} from '@/pages/app/lib/composeRender';
import { useCredits } from '@/hooks/useCredits';
import { useConfirm } from '@/hooks/useConfirm';
import PostToSocialModal from '@/pages/gallery/components/PostToSocialModal';
import { useSocialPublishingEnabled } from '@/hooks/useFeatureFlags';

type ProtoAspect = '1:1' | '4:5' | '2:3' | '9:16' | '16:9' | '1.91:1';
const ASPECT_W_OVER_H: Record<ProtoAspect, number> = {
  '1:1': 1, '4:5': 4 / 5, '2:3': 2 / 3, '9:16': 9 / 16, '16:9': 16 / 9, '1.91:1': 1.91,
};

// Floating side panels — wide monitors only. Below FLOAT_MIN we keep the docked
// rails (same as today), so narrow screens are unaffected. In float mode the
// artboard is capped + centered with reserved side gutters so the panels hug it.
const FLOAT_MIN = 1500;         // ≥ this viewport width → floating panels
const PANEL_GAP = 20;           // gap between the artboard and each panel
const FLOAT_MAX_BOX = 780;      // cap the artboard so it isn't huge + leaves room
const FLOAT_SIDE_RESERVE = 656; // 2 × (widest panel 288 + 2×gap) — keeps it centered
const FLOAT_TOP = 24;           // gap below the top bar (content is top-aligned)
const FLOAT_VERT_RESERVE = 130; // top gap + one-row bottom bar so the image fits between
const DOCK_GUTTER = 10;         // embedded: breathing room around the image (all 4 sides)
const DOCK_PANEL_MIN = 200;     // embedded: min width of the LAYERS + AI side panels
const DOCK_PROPS_W = 256;       // embedded: width of the PROPS panel when open (w-64)

const FONT_OPTIONS = [
  'Manrope', 'Inter', 'DM Sans', 'Archivo', 'Space Grotesk', 'Fraunces',
  'Cormorant Garamond', 'Bodoni Moda', 'Playfair Display', 'Instrument Serif',
  'Bebas Neue', 'Anton', 'Chivo', 'Work Sans', 'Syne', 'Poppins', 'Lora',
  'Montserrat', 'Lato', 'Raleway', 'Merriweather', 'Roboto', 'Open Sans',
];
const WEIGHT_OPTIONS: { v: TextOverlay['fontWeight']; l: string }[] = [
  { v: 'normal', l: 'Reg' }, { v: 'semibold', l: 'Semi' }, { v: 'bold', l: 'Bold' },
];
const CASE_OPTIONS: { v: TextOverlay['textTransform']; l: string }[] = [
  { v: 'none', l: 'Aa' }, { v: 'uppercase', l: 'AA' }, { v: 'lowercase', l: 'aa' },
];
const ALIGN_OPTIONS: { v: TextOverlay['textAlign']; icon: string }[] = [
  { v: 'left', icon: 'ri-align-left' }, { v: 'center', icon: 'ri-align-center' }, { v: 'right', icon: 'ri-align-right' },
];
const PLACEMENT_CELLS = [
  'top-left', 'top-center', 'top-right',
  'middle-left', 'middle-center', 'middle-right',
  'bottom-left', 'bottom-center', 'bottom-right',
];
const BW_SWATCHES = ['#FFFFFF', '#000000'];

// ── App-tokened primitives ───────────────────────────────────────────────────
const railBtn =
  'w-full h-10 inline-flex items-center gap-2 px-3 rounded-lg border-2 border-dashed border-surface-dark/60 text-dark-muted text-xs font-bold uppercase tracking-wide hover:border-primary/50 hover:text-primary transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';
const chip = (active: boolean) =>
  `h-8 min-w-8 px-2 inline-flex items-center justify-center rounded-md text-xs font-bold cursor-pointer transition-colors border ${
    active ? 'bg-primary-light text-primary border-primary' : 'bg-white text-dark border-surface-dark/40 hover:border-primary/40'
  }`;
const sectionLabel = 'text-[10px] font-bold uppercase tracking-[0.14em] text-dark-muted';

export interface CreativeEditorProps {
  /** Post to load. When this prop changes, the editor re-loads in place. */
  initialPostId: string;
  /** Optional seed for first paint — when its id matches initialPostId the
   *  editor hydrates from it directly, skipping the getPost fetch (no spinner
   *  flash). Used by the wizard, which already has the just-saved post. */
  initialPost?: Post;
  /** Carousel seed — the in-memory frame images (data URLs). When length > 1,
   *  the editor is a CAROUSEL: a frame thumbnail strip + per-frame overlays
   *  (each overlay carries its own imageIndex) + per-frame AI-edit/re-render,
   *  saved/downloaded as a frame1..N.png zip. Omitted / length ≤ 1 → the
   *  single-image editor (identical to before). The wizard passes its
   *  displayFrames here; /creatives passes nothing (loads by id). */
  initialFrames?: string[];
  /** Top-left back button label + icon. */
  backLabel?: string;
  backIcon?: string;
  /** Called by the back button AFTER the unsaved-changes guard. Route mode →
   *  navigate('/gallery'); embed mode → return to the template picker. */
  onBack: () => void;
  /** Optional URL sync when the active post forks (save) or switches (version). */
  onActivePostChange?: (postId: string) => void;
  /** Show the header Delete button (hidden mid-wizard). */
  showDelete?: boolean;
  /** After a successful delete. Defaults to onBack. */
  onDeleted?: () => void;
  /** true → fill the positioned parent (absolute inset-0); false → full-screen
   *  fixed inset-0 (route mode). */
  embedded?: boolean;
  /** Body layout. 'classic' = the floating-panel layout (/creatives:id today).
   *  'v5' = the docked 4-column layout (LAYERS · PROPS · IMAGE · vertical AI +
   *  Tools) built for the Product Shot finish step. `embedded` implies 'v5'.
   *  Flip /creatives:id to `variant="v5"` to adopt this everywhere. */
  variant?: 'classic' | 'v5';
  /** AI-written copy for the "AI text" button + new-text-layer seeding. When
   *  omitted, falls back to the post's stored headline/tagline. */
  suggestedHeadline?: string | null;
  suggestedTagline?: string | null;
  /** Fetch a FRESH batch of AI copy lines on demand (the wizard has the product
   *  file). Returns several distinct headline/tagline options so the "AI text"
   *  button gives new copy on each click instead of repeating. When omitted, the
   *  button cycles the stored copy. */
  onRequestAiText?: () => Promise<string[] | null>;
}

export default function CreativeEditor({
  initialPostId,
  initialPost,
  initialFrames,
  backLabel = 'My Creatives',
  backIcon = 'ri-arrow-left-line',
  onBack,
  onActivePostChange,
  showDelete = true,
  onDeleted,
  embedded = false,
  variant = 'classic',
  suggestedHeadline = null,
  suggestedTagline = null,
  onRequestAiText,
}: CreativeEditorProps) {
  // v5 = the docked 4-column layout. Embedded (Product Shot) always uses it; a
  // full-screen route can opt in with variant="v5" (the /creatives:id swap).
  const dockedLayout = variant === 'v5' || embedded;
  // The editor owns the active post id so version-switch + save-fork happen in
  // place (no route dependency). Parent may sync a URL via onActivePostChange.
  const [postId, setPostId] = useState(initialPostId);
  useEffect(() => { setPostId(initialPostId); }, [initialPostId]);
  // Account-aware: FB/IG publish is hidden for real users, on for reviewer/team.
  const SOCIAL_PUBLISHING_ENABLED = useSocialPublishingEnabled();
  // Seed for first paint (read via ref so it never re-fires the load effect,
  // which would wipe in-progress edits when the parent re-renders).
  const initialPostRef = useRef(initialPost);
  initialPostRef.current = initialPost;
  const initialFramesRef = useRef(initialFrames);
  initialFramesRef.current = initialFrames;
  const switchToPost = useCallback((id: string) => {
    setPostId(id);
    onActivePostChange?.(id);
  }, [onActivePostChange]);

  const { refresh: refreshCredits } = useCredits();
  const confirm = useConfirm();

  // ── Post + editor state ────────────────────────────────────────────────
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(!(initialPost && initialPost.id === initialPostId));
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<Post[]>([]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const versionsRef = useRef<HTMLDivElement>(null);

  // ── Frames (single OR carousel) ───────────────────────────────────────
  // One string per frame. Single-image editor = a 1-element array (behaves
  // exactly as the old single `frame`). Carousel = N frames + a thumbnail
  // strip; overlays are tagged with imageIndex so each frame keeps its own.
  const [frames, setFrames] = useState<string[]>(['']);
  const [activeFrameIdx, setActiveFrameIdx] = useState(0);
  const isCarousel = frames.length > 1;
  const frame = frames[activeFrameIdx] ?? '';
  // setFrame mutates ONLY the active frame — every existing caller (load, AI
  // edit, re-render, marquee) updates whichever frame is on screen.
  const setFrame = (v: string | ((prev: string) => string)) =>
    setFrames((prev) => {
      const i = activeFrameIdx;
      const cur = prev[i] ?? '';
      const nv = typeof v === 'function' ? v(cur) : v;
      if (nv === cur) return prev;
      const copy = [...prev];
      copy[i] = nv;
      return copy;
    });
  // Flat overlay array across ALL frames; each overlay's imageIndex says which
  // frame it belongs to. `overlays` below is filtered to the active frame for
  // rendering/editing (currentOverlays), so single-frame code is unchanged.
  const [overlays, setOverlays] = useState<TextOverlay[]>([]);
  const [logoOverlays, setLogoOverlays] = useState<LogoOverlay[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null);

  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiTextBusy, setAiTextBusy] = useState(false); // "AI text" button fetch
  const aiTextQueueRef = useRef<string[]>([]);          // pending AI copy lines
  const [retryQ, setRetryQ] = useState<'low' | 'medium' | 'high' | null>(null);
  const [opError, setOpError] = useState<string | null>(null);
  const [marqueeMode, setMarqueeMode] = useState(false);
  const [marqueeApplying, setMarqueeApplying] = useState(false);
  const [marqueePreview, setMarqueePreview] = useState<
    { originalUrl: string; dirtyBefore: boolean } | null
  >(null);

  const [mobileTab, setMobileTab] = useState<'layers' | 'props'>('layers');
  const [publishOpen, setPublishOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Edit-baked-text: after prepare-text-edit swaps in the clean plate +
  // editable overlay_json (Model A — V5's native shape), re-hydrate the editor
  // IN PLACE via a bumped reloadTick so the user stays in this V5 editor.
  const pendingHydrateRef = useRef<Post | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [preparingText, setPreparingText] = useState(false);
  const [fullscreenUrl, setFullscreenUrl] = useState<string | null>(null);

  const stageRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null); // v5 docked row (all 4 columns)
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const [rowW, setRowW] = useState(0); // v5: total width available for the row
  const [previewW, setPreviewW] = useState(0);
  const [viewportW, setViewportW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));

  const overlaysRef = useRef(overlays); overlaysRef.current = overlays;
  const logoOverlaysRef = useRef(logoOverlays); logoOverlaysRef.current = logoOverlays;
  const activeIdRef = useRef(activeId); activeIdRef.current = activeId;
  // Overlays belonging to the frame currently on screen. For a single-image
  // editor every overlay has imageIndex 0 so this equals `overlays` — the
  // filter is a no-op. Carousels keep each frame's overlays separate.
  const currentOverlays = useMemo(
    () => overlays.filter((o) => (o.imageIndex ?? 0) === activeFrameIdx),
    [overlays, activeFrameIdx],
  );
  const currentLogos = useMemo(
    () => logoOverlays.filter((l) => (l.imageIndex ?? 0) === activeFrameIdx),
    [logoOverlays, activeFrameIdx],
  );
  const draggingLogoRef = useRef(false);
  const dragBoxRef = useRef<{ w: number; h: number } | null>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);

  const aspect = (['1:1', '4:5', '2:3', '9:16', '16:9', '1.91:1'].includes(post?.aspect_ratio ?? '')
    ? post?.aspect_ratio : '1:1') as ProtoAspect;

  // ── Load post + hydrate Model-A overlays ───────────────────────────────
  useEffect(() => {
    if (!postId) return;
    let cancelled = false;
    const createdUrls: string[] = [];
    const hydrate = (p: Post) => {
      setPost(p);
      setOverlays(Array.isArray(p.overlay_json) ? (p.overlay_json as TextOverlay[]) : []);
      setLogoOverlays(Array.isArray(p.logo_overlay_json) ? (p.logo_overlay_json as LogoOverlay[]) : []);
      setActiveId(null); setDirty(false); setMarqueePreview(null); setError(null);
      setActiveFrameIdx(0);
      // 1) Carousel handed to us in memory (wizard) → no fetch/unzip.
      const seedFrames = initialFramesRef.current;
      if (seedFrames && seedFrames.length > 1) {
        setFrames(seedFrames); setLoading(false); return;
      }
      // 2) A saved carousel post is a .zip → unzip client-side into frames.
      const isZip = p.image_path?.toLowerCase().endsWith('.zip') ?? false;
      if (isZip) {
        setLoading(true);
        const zipUrl = p.base_image_url || p.image_url || '';
        (async () => {
          try {
            const buf = new Uint8Array(await (await fetch(zipUrl)).arrayBuffer());
            const { unzipSync } = await import('fflate');
            const entries = unzipSync(buf);
            const pngs = Object.entries(entries)
              .filter(([n]) => n.toLowerCase().endsWith('.png'))
              .sort(([a], [b]) => a.localeCompare(b));
            const urls = pngs.map(([, bytes]) => {
              const url = URL.createObjectURL(new Blob([bytes], { type: 'image/png' }));
              createdUrls.push(url); return url;
            });
            if (!cancelled) { setFrames(urls.length ? urls : ['']); setLoading(false); }
          } catch (err) {
            if (!cancelled) { setError(err instanceof Error ? err.message : 'Could not open this carousel.'); setLoading(false); }
          }
        })();
        return;
      }
      // 3) Single image.
      setFrames([p.base_image_url || p.image_url || '']); setLoading(false);
    };
    // Re-hydrate after an in-place update (Edit Baked Text swapped in the clean
    // plate + overlay_json). Takes priority over the initial seed; consumed once.
    const pending = pendingHydrateRef.current;
    if (pending && pending.id === postId) {
      pendingHydrateRef.current = null;
      hydrate(pending);
      return () => { cancelled = true; createdUrls.forEach((u) => URL.revokeObjectURL(u)); };
    }
    // Parent handed us this exact post → hydrate directly, no fetch (no flash).
    const seed = initialPostRef.current;
    if (seed && seed.id === postId) {
      hydrate(seed);
      return () => { cancelled = true; createdUrls.forEach((u) => URL.revokeObjectURL(u)); };
    }
    setLoading(true);
    setError(null);
    getPost(postId)
      .then((p) => { if (!cancelled) hydrate(p); })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Could not load this creative.');
        setLoading(false);
      });
    return () => { cancelled = true; createdUrls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [postId, reloadTick]);

  useEffect(() => {
    getBrand().then((b) => setBrandLogoUrl(b?.logoUrl ?? null)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!postId) return;
    listCreativeVersions(postId).then(setVersions).catch(() => setVersions([]));
  }, [postId]);

  // ── Canvas fit ─────────────────────────────────────────────────────────
  // Keyed on `loading` AND `post`: the <main> stage renders only once
  // (loading===false && post!=null). In embed/seed mode `loading` starts false
  // but `post` is null on the first render (hydrated in the load effect), so a
  // `[loading]`-only dep would run against no stage and never re-fire → blank
  // canvas. Depending on `post` re-measures the instant the stage appears.
  useEffect(() => {
    if (loading || !post) return;
    const el = stageRef.current;
    if (!el) return;
    const update = () => { const r = el.getBoundingClientRect(); setStageSize({ w: r.width, h: r.height }); };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, post]);
  // v5: measure the full docked row so the image can be capped by the WIDTH
  // budget (row − both side panels − the props panel) — otherwise a tall
  // viewport makes the height-driven image so wide the right panel is clipped.
  useEffect(() => {
    if (loading || !post) return;
    const el = rowRef.current;
    if (!el) return;
    const update = () => setRowW(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, post]);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => setPreviewW(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stageSize]);
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // v5 (docked) never uses the floating panels — the image fills its main by
  // height and the side panels flex-fill the remainder.
  const floatMode = !dockedLayout && viewportW >= FLOAT_MIN;
  const wOverH = ASPECT_W_OVER_H[aspect] ?? 1;
  // v5 docked: the image is sized by HEIGHT first (biggest the card allows,
  // never shrinks for the panels) BUT also capped by the horizontal WIDTH
  // budget = row − both side panels (min) − the props panel (when open) −
  // gutters. Without the width cap a tall viewport makes the height-driven
  // image so wide the four columns overflow and the right panel is clipped.
  // Float mode: <main> is full width (asides absolute), so reserve side gutters.
  const propsOpenNow = dockedLayout && activeId != null
    && (overlays.some((o) => o.id === activeId) || logoOverlays.some((l) => l.id === activeId));
  // Panels are `hidden md:flex` — only reserve their width at md+ (else mobile,
  // where only the image shows, would over-subtract and zero the image). The
  // extra 16px is a safety margin so sub-pixel rounding never overflows → clip.
  const dockWidthBudget = (rowW > 0 && viewportW >= 768)
    ? rowW - DOCK_PANEL_MIN * 2 - (propsOpenNow ? DOCK_PROPS_W : 0) - DOCK_GUTTER * 2 - 16
    : Infinity;
  // Carousel shows a thumbnail strip under the image — reserve vertical room
  // so the artboard shrinks to leave space instead of overflowing the stage.
  const THUMB_RESERVE = isCarousel ? 84 : 0;
  const boxW = dockedLayout
    ? (stageSize.h > 0
        ? Math.max(0, Math.floor(Math.min((stageSize.h - DOCK_GUTTER * 2 - THUMB_RESERVE) * wOverH, dockWidthBudget)))
        : 0)
    : (stageSize.w > 0 && stageSize.h > 0
        ? (floatMode
            ? Math.floor(Math.min((stageSize.h - FLOAT_VERT_RESERVE - THUMB_RESERVE) * wOverH, stageSize.w - FLOAT_SIDE_RESERVE, FLOAT_MAX_BOX))
            : Math.floor(Math.min(stageSize.w, (stageSize.h - THUMB_RESERVE) * wOverH)))
        : 0);
  const boxH = boxW > 0 ? Math.round(boxW / wOverH) : 0; // artboard height → float panels match it
  const previewFontScale = previewW > 0 ? previewW / REFERENCE_CANVAS_WIDTH : 1;

  // ── Drag-to-position (canonical handler) ───────────────────────────────
  useEffect(() => {
    if (!draggingId) return;
    const move = (clientX: number, clientY: number) => {
      const rect = previewRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return;
      const cx = (clientX - rect.left) / rect.width;
      const cy = (clientY - rect.top) / rect.height;
      const off = dragOffsetRef.current;
      const tx = off ? cx - off.dx : cx;
      const ty = off ? cy - off.dy : cy;
      const box = dragBoxRef.current;
      if (draggingLogoRef.current) {
        const logo = logoOverlaysRef.current.find((l) => l.id === draggingId);
        if (!logo) return;
        const wFrac = box ? box.w / rect.width : logo.widthFrac;
        const hFrac = box ? box.h / rect.height : logo.widthFrac;
        setLogoOverlays(logoOverlaysRef.current.map((l) =>
          l.id === draggingId
            ? { ...l, x: Math.max(0, Math.min(1 - wFrac, tx)), y: Math.max(0, Math.min(1 - hFrac, ty)), positionId: '' }
            : l));
        return;
      }
      const dragged = overlaysRef.current.find((o) => o.id === draggingId);
      if (!dragged) return;
      const wFrac = box ? box.w / rect.width : 0;
      const hFrac = box ? box.h / rect.height : 0;
      const { x, y } = clampAnchor(tx, ty, wFrac, hFrac, dragged.anchor);
      setOverlays(overlaysRef.current.map((o) => o.id === draggingId ? { ...o, x, y, positionId: '' } : o));
    };
    const onMouse = (e: MouseEvent) => move(e.clientX, e.clientY);
    const onTouch = (e: TouchEvent) => { const t = e.touches[0]; if (t) move(t.clientX, t.clientY); };
    const onUp = () => { setDraggingId(null); dragBoxRef.current = null; dragOffsetRef.current = null; draggingLogoRef.current = false; };
    window.addEventListener('mousemove', onMouse);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouch, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouch);
      window.removeEventListener('touchend', onUp);
    };
  }, [draggingId]);

  // ── Derived ────────────────────────────────────────────────────────────
  // Layer list shows only the ACTIVE frame's layers (single-frame → all).
  const ctaLayer = currentOverlays.find((o) => o.id.startsWith('cta-')) || null;
  const textLayers = currentOverlays.filter((o) => !o.id.startsWith('cta-'));
  const logoLayer = currentLogos[0] || null;
  const refH = aspectToRefHeight(aspect);
  const resolved = useMemo(
    () => resolveStackedLayers(currentOverlays, REFERENCE_CANVAS_WIDTH, refH) as TextOverlay[],
    [currentOverlays, refH],
  );
  const activeOverlay = overlays.find((o) => o.id === activeId) || null;
  const activeLogo = logoOverlays.find((l) => l.id === activeId) || null;

  const renderBusy = retryQ !== null || aiBusy || marqueeApplying || preparingText;
  const mutating = renderBusy || marqueePreview !== null;
  const busyLabel = retryQ ? `Re-rendering (${retryQ})…` : aiBusy ? 'Applying your edit…' : marqueeApplying ? 'Editing region…' : preparingText ? 'Making text editable…' : '';

  // ── Layer mutation ─────────────────────────────────────────────────────
  const markDirty = () => setDirty(true);
  const updateOverlay = (idl: string, u: Partial<TextOverlay>) => {
    setOverlays((prev) => prev.map((o) => (o.id === idl ? { ...o, ...u } : o))); markDirty();
  };
  const updateLogo = (idl: string, u: Partial<LogoOverlay>) => {
    setLogoOverlays((prev) => prev.map((l) => (l.id === idl ? { ...l, ...u } : l))); markDirty();
  };
  const removeLayer = (idl: string) => {
    setOverlays((prev) => prev.filter((o) => o.id !== idl));
    setLogoOverlays((prev) => prev.filter((l) => l.id !== idl));
    if (activeIdRef.current === idl) setActiveId(null);
    markDirty();
  };
  // AI copy for seeding new text + the "AI text" button. Prefer the props
  // (fresh wizard concept); fall back to the post's stored headline/tagline.
  const aiHeadline = suggestedHeadline ?? post?.headline ?? null;
  const aiTagline = suggestedTagline ?? post?.tagline ?? null;
  // "Edit baked text" is offered on any flat render whose copy is baked INTO the
  // pixels — i.e. NOT a Model-B text_overlay tile, and NOT a post that already
  // carries editable overlay_json layers (its text is already editable, or it was
  // already lifted). We DON'T gate on a stored headline/tagline: many copy-bearing
  // renders (recipes / brand-setup) don't persist those, yet clearly have baked
  // text. The backend vision-reads the image directly and, if it finds no editable
  // text, returns 422 + auto-refunds (no charge) — so showing it broadly is safe.
  const hasBakedText =
    !post?.text_overlay &&
    !(Array.isArray(post?.overlay_json) && post.overlay_json.length > 0);
  // "AI text" is always available: the wizard supplies a live fetcher, and
  // everywhere else the editor self-serves fresh copy from the creative's own
  // rendered image (see fetchAiTextBatch). Stored copy is the last-resort seed.
  const hasAiText = !!(aiHeadline || aiTagline) || !!onRequestAiText || !!frame;
  const addTextLayer = () => {
    const used = new Set(textLayers.map((o) => o.text));
    const text = aiHeadline && !used.has(aiHeadline) ? aiHeadline
      : aiTagline && !used.has(aiTagline) ? aiTagline : 'Your text here';
    const next: TextOverlay = {
      kind: 'text', id: `text-${Date.now()}`, imageIndex: activeFrameIdx, text,
      x: 0.5, y: 0.4, anchor: 'mc', maxWidthFrac: 0.82, textAlign: 'center',
      fontSize: 64, fontWeight: 'bold', fontFamily: 'Manrope', color: '#FFFFFF',
      textTransform: 'uppercase', maxLines: 3,
    };
    setOverlays((prev) => [...prev, next]); setActiveId(next.id); markDirty();
  };
  // Fetch a FRESH batch of distinct copy lines. Prefer the wizard's fetcher (it
  // has the raw product file); otherwise self-serve by running suggest-copy on
  // the creative's OWN rendered image — so /creatives:id and /editor-v4 get real
  // AI text too, not just the wizard embed. frameCount:6 → distinct arc frames →
  // ~12 deduped headline/tagline lines.
  const fetchAiTextBatch = async (): Promise<string[]> => {
    if (onRequestAiText) {
      const lines = await onRequestAiText();
      if (lines?.length) return lines;
    } else if (frame) {
      try {
        const blob = await (await fetch(frame)).blob();
        const file = new File([blob], 'creative.png', { type: blob.type || 'image/png' });
        const res = await suggestCopy({ productFile: file, productName: post?.title || '', productDescription: '', frameCount: 6 });
        const seen = new Set<string>(); const out: string[] = [];
        for (const f of res.frames ?? []) {
          for (const s of [f.headline, f.tagline]) {
            const t = s?.trim(); if (!t) continue;
            const k = t.toLowerCase(); if (seen.has(k)) continue;
            seen.add(k); out.push(t);
          }
        }
        if (out.length) return out;
      } catch { /* fall through to stored copy */ }
    }
    return [aiHeadline, aiTagline].filter((s): s is string => !!s);
  };

  // "AI text" — fill the selected layer with fresh AI copy. Drains a queue of
  // lines; when empty it fetches a FRESH BATCH via fetchAiTextBatch, so repeated
  // clicks give NEW copy instead of repeating. Skips a just-inserted duplicate
  // so a click always visibly changes the text.
  const aiFillText = async (id: string) => {
    if (aiTextBusy) return;
    const ov0 = overlaysRef.current.find((o) => o.id === id);
    if (!ov0) return;
    if (aiTextQueueRef.current.length === 0) {
      setAiTextBusy(true);
      try {
        const lines = await fetchAiTextBatch();
        if (lines.length) aiTextQueueRef.current.push(...lines);
      } catch { /* leave queue empty */ }
      finally { setAiTextBusy(false); }
    }
    // Pull the next line that differs from what's already shown.
    const cur = (overlaysRef.current.find((o) => o.id === id)?.text ?? '').trim().toLowerCase();
    let next = aiTextQueueRef.current.shift();
    if (next && next.trim().toLowerCase() === cur && aiTextQueueRef.current.length > 0) {
      next = aiTextQueueRef.current.shift();
    }
    if (next) updateOverlay(id, { text: next });
  };
  const addCtaLayer = () => {
    if (ctaLayer) { setActiveId(ctaLayer.id); return; }
    const next: TextOverlay = {
      kind: 'text', id: `cta-${Date.now()}`, imageIndex: activeFrameIdx, text: 'SHOP NOW',
      x: 0.5, y: 0.84, anchor: 'mc', maxWidthFrac: 0.6, textAlign: 'center',
      fontSize: 34, fontWeight: 'bold', fontFamily: 'Manrope', color: '#FFFFFF',
      textTransform: 'uppercase', maxLines: 1, backgroundColor: '#0F0F1E', backgroundPadding: 18,
    };
    setOverlays((prev) => [...prev, next]); setActiveId(next.id); markDirty();
  };
  const addLogoLayer = () => {
    if (logoLayer) { setActiveId(logoLayer.id); return; }
    if (!brandLogoUrl) return;
    const w = 0.18;
    const next: LogoOverlay = {
      id: `logo-${Date.now()}`, imageIndex: activeFrameIdx, src: brandLogoUrl,
      x: 1 - w - 0.04, y: 1 - w - 0.04, widthFrac: w, opacity: 1, tint: 'original',
    };
    setLogoOverlays((prev) => [...prev, next]); setActiveId(next.id); markDirty();
  };
  const placeTextAt = (posId: string) => { if (activeOverlay) updateOverlay(activeOverlay.id, snapForPosition(posId)); };
  const placeLogoAt = (posId: string) => {
    const logo = logoOverlaysRef.current.find((l) => l.id === activeIdRef.current);
    const preview = previewRef.current;
    if (!logo || !preview) return;
    const pr = preview.getBoundingClientRect();
    if (pr.width === 0 || pr.height === 0) return;
    const el = preview.querySelector(`[data-logo-id="${logo.id}"]`) as HTMLElement | null;
    if (!el) return;
    const lr = el.getBoundingClientRect();
    const wFrac = lr.width / pr.width, hFrac = lr.height / pr.height;
    const pad = OVERLAY_PAD_FRAC;
    const left = posId.includes('left'), right = posId.includes('right');
    const top = posId.startsWith('top'), bottom = posId.startsWith('bottom');
    const x = left ? pad : right ? 1 - pad - wFrac : 0.5 - wFrac / 2;
    const y = top ? pad : bottom ? 1 - pad - hFrac : 0.5 - hFrac / 2;
    updateLogo(logo.id, { x: Math.max(0, Math.min(1 - wFrac, x)), y: Math.max(0, Math.min(1 - hFrac, y)), positionId: posId });
  };
  const logoFilter = (t: LogoTint | undefined) =>
    t === 'black' ? 'brightness(0)' : t === 'white' ? 'brightness(0) invert(1)' : undefined;

  // ── Save / AI / re-render / marquee / download ─────────────────────────
  const doSave = useCallback(async (): Promise<Post | null> => {
    if (!post?.id || !frame) return null;
    if (!dirty) return post;
    setSaving(true);
    const urls: string[] = [];
    try {
      // ── Carousel → flatten EVERY frame (each with its own imageIndex-scoped
      // overlays) and save a frame1..N.png zip. Overlays are baked in (no
      // overlay_json round-trip for carousels — matches the wizard's zip save).
      if (isCarousel) {
        const { zip } = await import('fflate');
        // Fetch each frame into a same-origin object-URL first — a cross-origin
        // GCS URL (e.g. a marquee preview) would taint the flatten canvas and
        // fail toBlob. (The single path below does the same via `src`.)
        const blobs = await Promise.all(
          frames.map(async (src, i) => {
            const objUrl = URL.createObjectURL(await (await fetch(src)).blob());
            urls.push(objUrl);
            return flattenComposedFrame({ imageSrc: objUrl, frameIndex: i, overlays, shapeOverlays: [], logoOverlays });
          }),
        );
        const entries: Record<string, Uint8Array> = {};
        for (let i = 0; i < blobs.length; i++) {
          const b = blobs[i];
          if (!b) throw new Error(`Could not render frame ${i + 1}`);
          entries[`frame${i + 1}.png`] = new Uint8Array(await b.arrayBuffer());
        }
        const zipped: Uint8Array = await new Promise((resolve, reject) => {
          zip(entries, (err, data) => (err ? reject(err) : resolve(data)));
        });
        const zipBlob = new Blob([zipped.buffer as ArrayBuffer], { type: 'application/zip' });
        const newPost = await saveCreativeVersion({
          sourcePostId: post.id, blob: zipBlob,
          contentType: 'application/zip', fileExt: 'zip',
        });
        setDirty(false);
        return newPost;
      }
      // ── Single image (Option B: text-free base + overlay_json on top).
      const src = await (await fetch(frame)).blob();
      let outputBlob = src;
      if (overlays.length > 0 || logoOverlays.length > 0) {
        const objUrl = URL.createObjectURL(src); urls.push(objUrl);
        const flat = await flattenComposedFrame({ imageSrc: objUrl, frameIndex: 0, overlays, shapeOverlays: [], logoOverlays });
        if (!flat) throw new Error('Could not flatten the image.');
        outputBlob = flat;
      }
      const newPost = await saveCreativeVersion({
        sourcePostId: post.id, blob: outputBlob, baseBlob: src,
        overlayJson: overlays, logoOverlayJson: logoOverlays,
      });
      setDirty(false);
      return newPost;
    } catch (e) {
      setOpError(e instanceof Error ? e.message : 'Save failed');
      return null;
    } finally {
      for (const u of urls) URL.revokeObjectURL(u);
      setSaving(false);
    }
  }, [post, frame, dirty, overlays, logoOverlays, isCarousel, frames]);

  const handleSave = useCallback(async () => {
    const np = await doSave();
    if (np && np.id !== postId) switchToPost(np.id);
  }, [doSave, switchToPost, postId]);

  const handleAiEdit = useCallback(async () => {
    const prompt = aiPrompt.trim();
    if (!prompt || !frame) return;
    setAiBusy(true); setOpError(null);
    try {
      const blob = await (await fetch(frame)).blob();
      const r = await wizardEditImage({ image: blob, prompt, aspectRatio: post?.aspect_ratio ?? '1:1', compositeLogo: false });
      setFrame(`data:image/png;base64,${r.image_b64}`);
      setAiPrompt(''); setDirty(true); void refreshCredits();
    } catch (e) { setOpError(e instanceof Error ? e.message : 'Edit failed'); }
    finally { setAiBusy(false); }
  }, [aiPrompt, frame, post?.aspect_ratio, refreshCredits]);

  const handleReRender = useCallback(async (q: 'low' | 'medium' | 'high') => {
    if (!frame || retryQ) return;
    setRetryQ(q); setOpError(null);
    try {
      const blob = await (await fetch(frame)).blob();
      const r = await wizardEditImage({
        image: blob,
        prompt: 'Re-render this advertising image with enhanced clarity, sharper detail, and refined typography. Preserve the composition, subject, layout, and palette exactly — only improve fidelity.',
        aspectRatio: post?.aspect_ratio ?? '1:1', compositeLogo: false, quality: q,
      });
      setFrame(`data:image/png;base64,${r.image_b64}`);
      setDirty(true); void refreshCredits();
    } catch (e) { setOpError(e instanceof Error ? e.message : 'Re-render failed'); }
    finally { setRetryQ(null); }
  }, [frame, retryQ, post?.aspect_ratio, refreshCredits]);

  const handleMarqueeApply = useCallback(async (
    rect: { x: number; y: number; w: number; h: number }, instruction: string,
  ) => {
    if (!post || marqueeApplying || marqueePreview) return;
    const originalUrl = frame; const dirtyBefore = dirty;
    setMarqueeApplying(true); setOpError(null);
    try {
      // Send the CURRENT frame's bytes so the region edit targets exactly what's
      // on screen — the right carousel frame, and the live (possibly AI-edited)
      // image rather than a stale server-side base.
      const blob = await (await fetch(frame)).blob();
      const imageB64 = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = () => reject(fr.error);
        fr.readAsDataURL(blob);
      });
      const { previewUrl } = await marqueeEditPreview(post.id, rect, instruction, {
        imageB64, frameIndex: activeFrameIdx,
      });
      setFrame(previewUrl); setDirty(true); setMarqueeMode(false);
      setMarqueePreview({ originalUrl, dirtyBefore }); void refreshCredits();
    } catch (e) { setOpError(e instanceof Error ? e.message : 'Region edit failed'); }
    finally { setMarqueeApplying(false); }
  }, [post, marqueeApplying, marqueePreview, frame, dirty, refreshCredits, activeFrameIdx]);

  const handleMarqueeKeep = useCallback(async () => {
    if (!marqueePreview) return;
    const np = await doSave();
    if (np && np.id !== postId) switchToPost(np.id);
    else setMarqueePreview(null);
  }, [marqueePreview, doSave, switchToPost, postId]);

  const handleMarqueeDiscard = useCallback(() => {
    if (!marqueePreview) return;
    setFrame(marqueePreview.originalUrl); setDirty(marqueePreview.dirtyBefore);
    setMarqueePreview(null); setOpError(null);
  }, [marqueePreview]);

  const handleDownload = useCallback(async () => {
    if (!frame) return;
    setDownloading(true);
    const urls: string[] = [];
    const base = (post?.title || 'creative').replace(/[^a-z0-9]+/gi, '-');
    try {
      // Carousel → a frame1..N.png zip of the flattened frames.
      if (isCarousel) {
        const { zip } = await import('fflate');
        // Same-origin object-URLs so a cross-origin frame (marquee preview) can't
        // taint the flatten canvas.
        const blobs = await Promise.all(
          frames.map(async (src, i) => {
            const objUrl = URL.createObjectURL(await (await fetch(src)).blob());
            urls.push(objUrl);
            return flattenComposedFrame({ imageSrc: objUrl, frameIndex: i, overlays, shapeOverlays: [], logoOverlays });
          }),
        );
        const entries: Record<string, Uint8Array> = {};
        for (let i = 0; i < blobs.length; i++) {
          const b = blobs[i]; if (!b) continue;
          entries[`frame${i + 1}.png`] = new Uint8Array(await b.arrayBuffer());
        }
        const zipped: Uint8Array = await new Promise((resolve, reject) => {
          zip(entries, (err, data) => (err ? reject(err) : resolve(data)));
        });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([zipped.buffer as ArrayBuffer], { type: 'application/zip' }));
        urls.push(a.href);
        a.download = `${base}-carousel.zip`;
        a.click();
        return;
      }
      const src = await (await fetch(frame)).blob();
      let out = src;
      if (overlays.length > 0 || logoOverlays.length > 0) {
        const objUrl = URL.createObjectURL(src); urls.push(objUrl);
        const flat = await flattenComposedFrame({ imageSrc: objUrl, frameIndex: 0, overlays, shapeOverlays: [], logoOverlays });
        if (flat) out = flat;
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(out); urls.push(a.href);
      a.download = `${base}.png`;
      a.click();
    } finally {
      setTimeout(() => urls.forEach((u) => URL.revokeObjectURL(u)), 4000);
      setDownloading(false);
    }
  }, [frame, overlays, logoOverlays, post?.title, isCarousel, frames]);

  const handleDelete = useCallback(async () => {
    if (!post) return;
    const ok = await confirm({
      title: 'Delete this creative?',
      message: 'This will permanently remove it from My Creatives. Already-published posts on Facebook / Instagram remain live.',
      destructive: true, confirmLabel: 'Delete',
    });
    if (!ok) return;
    try { await deletePost(post.id); (onDeleted ?? onBack)(); }
    catch (e) { setOpError(e instanceof Error ? e.message : 'Delete failed'); }
  }, [post, confirm, onDeleted, onBack]);

  // Post — publish the SAVED image. If there are unsaved edits, save first
  // (forks a version + navigates); the user hits Post again on the saved one.
  const handlePost = useCallback(async () => {
    if (!post) return;
    if (dirty) { await handleSave(); return; }
    setPublishOpen(true);
  }, [post, dirty, handleSave]);

  // Edit Baked Text — lift the copy baked into the pixels into editable layers,
  // staying in THIS V5 editor. The backend removes the text (clean plate →
  // base_image_path) + vision-reads it into overlay_json (Model A, V5's native
  // shape), then we re-hydrate in place so the headline/subhead become draggable
  // TEXT layers on the left. 2 credits, idempotent (free if already prepared).
  const handleEditBakedText = useCallback(async () => {
    if (!post || mutating || preparingText || !hasBakedText) return;
    const ok = await confirm({
      title: 'Make the text editable?',
      message:
        'This lifts the headline/subhead into editable layers. It works best when '
        + 'the text sits clearly on the background — copy over the product may not '
        + 'lift cleanly. Costs 2 credits.',
      confirmLabel: 'Continue',
      cancelLabel: 'Cancel',
      icon: 'ri-text',
    });
    if (!ok) return;
    setPreparingText(true);
    try {
      // Carousels are per-slide: lift only the slide currently in view.
      const frameIndex = isCarousel ? activeFrameIdx : undefined;
      const updated = await prepareTextEdit(post.id, { frameIndex });
      pendingHydrateRef.current = updated;   // consumed by the load effect
      setReloadTick((t) => t + 1);           // → re-hydrate in place (stays in V5)
      void refreshCredits();
      setToast('Text is now editable — tweak the layers on the left.');
    } catch (e) {
      // 422 = no editable text found (auto-refunded); 504 timeout auto-refunds too.
      setToast(e instanceof Error ? e.message : 'Could not prepare text editing.');
      void refreshCredits();
    } finally {
      setPreparingText(false);
    }
  }, [post, mutating, preparingText, hasBakedText, isCarousel, activeFrameIdx, confirm, refreshCredits]);

  // Fullscreen preview — flatten the current frame + overlays and show it in a
  // lightbox so the user can eyeball the final composite at full size.
  const handleFullscreen = useCallback(async () => {
    if (!frame) return;
    try {
      const src = await (await fetch(frame)).blob();
      let out = src;
      if (overlays.length > 0 || logoOverlays.length > 0) {
        const objUrl = URL.createObjectURL(src);
        const flat = await flattenComposedFrame({ imageSrc: objUrl, frameIndex: 0, overlays, shapeOverlays: [], logoOverlays });
        URL.revokeObjectURL(objUrl);
        if (flat) out = flat;
      }
      setFullscreenUrl(URL.createObjectURL(out));
    } catch { /* noop */ }
  }, [frame, overlays, logoOverlays]);
  const closeFullscreen = useCallback(() => {
    setFullscreenUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
  }, []);

  const handleClose = useCallback(async () => {
    if (marqueePreview) {
      const ok = await confirm({ title: 'Discard the unsaved edit?', message: "You have an edit that isn't saved. Leaving discards it — the credit is already spent. Leave anyway?", confirmLabel: 'Leave', cancelLabel: 'Stay', destructive: true });
      if (!ok) return;
    } else if (dirty) {
      const ok = await confirm({ title: 'Leave without saving?', message: 'You have unsaved changes. Leave anyway?', confirmLabel: 'Leave', cancelLabel: 'Stay', destructive: true });
      if (!ok) return;
    }
    onBack();
  }, [marqueePreview, dirty, confirm, onBack]);

  useEffect(() => {
    if (!dirty && !marqueePreview) return;
    const onUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty, marqueePreview]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // Close the Versions dropdown on any click outside it. Capture phase so the
  // canvas/overlays' stopPropagation() on mousedown can't swallow the close.
  useEffect(() => {
    if (!versionsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!versionsRef.current?.contains(e.target as Node)) setVersionsOpen(false);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [versionsOpen]);

  useEffect(() => {
    if (!fullscreenUrl) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeFullscreen(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreenUrl, closeFullscreen]);

  // ── Render ─────────────────────────────────────────────────────────────
  // Non-embedded = full-screen. z-[60] so, when the wizard portals it over the
  // app (Product Shot finish), it covers the sidebar like /creatives:id.
  const shellClass = embedded ? 'absolute inset-0' : 'fixed inset-0 z-[60]';
  if (loading) {
    return <div className={`${shellClass} flex items-center justify-center bg-surface text-dark-muted`}><i className="ri-loader-4-line animate-spin text-2xl mr-2" /> Loading editor…</div>;
  }
  if (error || !post) {
    return (
      <div className={`${shellClass} flex flex-col items-center justify-center gap-4 bg-surface text-dark`}>
        <p>{error || 'Creative not found.'}</p>
        <button onClick={onBack} className="px-4 h-10 rounded-full bg-primary text-white text-sm font-bold cursor-pointer">{backLabel}</button>
      </div>
    );
  }

  const layersPanel = (
    <LayersPanel logoLayer={logoLayer} ctaLayer={ctaLayer} textLayers={textLayers}
      activeId={activeId} setActiveId={setActiveId}
      addLogoLayer={addLogoLayer} addCtaLayer={addCtaLayer} addTextLayer={addTextLayer}
      removeLayer={removeLayer} brandLogoUrl={brandLogoUrl} mutating={mutating}
      onEditBakedText={handleEditBakedText} hasBakedText={hasBakedText}
      onPost={handlePost} onDownload={handleDownload} downloading={downloading}
      onSave={handleSave} saving={saving} dirty={dirty} hideSave={dockedLayout} />
  );
  const propsPanel = (
    <PropertyPanel activeOverlay={activeOverlay} activeLogo={activeLogo} brandColors={[]}
      updateOverlay={updateOverlay} updateLogo={updateLogo} removeLayer={removeLayer}
      placeTextAt={placeTextAt} placeLogoAt={placeLogoAt}
      hasAiText={hasAiText} onAiFill={aiFillText} aiTextBusy={aiTextBusy} />
  );
  // Is a layer currently selected? Drives the docked (embedded) layout: props
  // panel opens, image shifts right, AI panel shrinks.
  const selected = !!(activeOverlay || activeLogo);
  // The artboard (image + all overlays / marquee / drag handlers). Extracted so
  // BOTH the float layout (/creatives) and the docked layout (Product Shot)
  // render the exact same canvas — no duplication.
  const artboard = boxW > 0 ? (
    <div ref={previewRef}
      className={`relative rounded-2xl overflow-hidden bg-gradient-to-br from-surface to-surface-dark/40 shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] ${aspectClass(aspect)} ${dockedLayout ? 'transition-[width] duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)]' : ''}`}
      style={{ width: `${boxW}px` }} onMouseDown={(e) => e.stopPropagation()}>
      {frame && <img src={frame} alt="Creative" className="w-full h-full object-contain" draggable={false} onMouseDown={() => setActiveId(null)} />}

      {!marqueeMode && !marqueePreview && (
        <button onClick={handleFullscreen} title="View full screen"
          className="absolute top-2.5 right-2.5 z-40 h-8 w-8 inline-flex items-center justify-center rounded-lg bg-white/90 backdrop-blur-sm shadow-md text-dark hover:text-primary cursor-pointer">
          <i className="ri-fullscreen-line" />
        </button>
      )}

      {renderBusy && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-dark/55 backdrop-blur-[2px]">
          <div className="w-12 h-12 rounded-full border-[3px] border-white/30 border-t-white animate-spin" />
          <p className="text-white text-sm font-bold drop-shadow" aria-live="polite">{busyLabel}</p>
          <p className="text-white/80 text-xs px-6 text-center max-w-[240px]">This can take ~25s — don&apos;t leave or you&apos;ll lose the credits.</p>
        </div>
      )}

      {!marqueePreview && (
        <MarqueeOverlay enabled={marqueeMode} applying={marqueeApplying} previewRef={previewRef}
          onApply={handleMarqueeApply} onCancel={() => setMarqueeMode(false)} />
      )}

      {marqueeMode && !marqueePreview && !marqueeApplying && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[55] flex items-center gap-2 pointer-events-none">
          <div className="px-5 py-3 rounded-2xl bg-dark/95 backdrop-blur-sm text-white text-sm sm:text-base font-semibold shadow-xl ring-1 ring-white/15 whitespace-nowrap flex items-center gap-2.5">
            <i className="ri-drag-move-2-line text-lg text-primary" /> Draw a rectangle over the area you want to edit
          </div>
          <button onClick={() => setMarqueeMode(false)}
            className="pointer-events-auto px-4 py-3 rounded-2xl bg-white text-dark hover:bg-surface-dark/20 text-sm sm:text-base font-bold shadow-xl ring-1 ring-surface-dark/30 whitespace-nowrap inline-flex items-center gap-1.5 cursor-pointer">
            <i className="ri-close-line text-lg" /> Cancel
          </button>
        </div>
      )}

      {marqueePreview && (
        <div className="absolute inset-x-0 bottom-0 z-[60]">
          <div className="m-3 rounded-2xl bg-dark/90 backdrop-blur-sm ring-1 ring-white/10 px-4 py-3 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-bold leading-tight">Apply these changes?</p>
              <p className="text-white/70 text-[11px] leading-tight mt-0.5">Keep saves it as a new version · Discard reverts.</p>
            </div>
            <button onClick={handleMarqueeDiscard} disabled={saving}
              className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold cursor-pointer disabled:opacity-40">
              <i className="ri-arrow-go-back-line" /> Discard
            </button>
            <button onClick={handleMarqueeKeep} disabled={saving}
              className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold cursor-pointer disabled:opacity-60">
              {saving ? <><i className="ri-loader-4-line animate-spin" /> Saving…</> : <><i className="ri-check-line" /> Keep</>}
            </button>
          </div>
        </div>
      )}

      {resolved.map((ov) => (
        <div key={ov.id}
          onMouseDown={(e) => {
            e.preventDefault(); e.stopPropagation();
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const pr = previewRef.current?.getBoundingClientRect();
            dragBoxRef.current = { w: r.width, h: r.height };
            if (pr && pr.width > 0 && pr.height > 0) dragOffsetRef.current = { dx: (e.clientX - pr.left) / pr.width - ov.x, dy: (e.clientY - pr.top) / pr.height - ov.y };
            if (ov.stackOnPrevious) setOverlays(overlaysRef.current.map((o) => o.id === ov.id ? { ...o, y: ov.y, stackOnPrevious: false } : o));
            setActiveId(ov.id); setDraggingId(ov.id);
          }}
          className={`absolute ${draggingId === ov.id ? 'cursor-grabbing' : 'cursor-grab'} ${activeId === ov.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-transparent' : ''}`}
          style={getTextLayerStyle(ov, { previewFontScale })}>
          {ov.text}
        </div>
      ))}

      {currentLogos.map((logo) => (
        <img key={logo.id} src={logo.src} alt="Logo" draggable={false} data-logo-id={logo.id}
          onMouseDown={(e) => {
            e.preventDefault(); e.stopPropagation();
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const pr = previewRef.current?.getBoundingClientRect();
            dragBoxRef.current = { w: r.width, h: r.height };
            if (pr && pr.width > 0 && pr.height > 0) dragOffsetRef.current = { dx: (e.clientX - pr.left) / pr.width - logo.x, dy: (e.clientY - pr.top) / pr.height - logo.y };
            draggingLogoRef.current = true;
            setActiveId(logo.id); setDraggingId(logo.id);
          }}
          className={`absolute ${activeId === logo.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-transparent' : ''} ${draggingId === logo.id ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{ left: `${logo.x * 100}%`, top: `${logo.y * 100}%`, width: `${logo.widthFrac * 100}%`, height: 'auto', opacity: logo.opacity, filter: logoFilter(logo.tint) }} />
      ))}
    </div>
  ) : null;

  // Carousel frame thumbnail strip — switches the active frame (each frame keeps
  // its own overlays + edits). Rendered under the artboard in both layouts.
  const frameThumbs = isCarousel ? (
    <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
      {frames.map((f, i) => (
        <button
          key={i}
          type="button"
          title={`Frame ${i + 1}`}
          onClick={() => { setActiveFrameIdx(i); setActiveId(null); setMarqueeMode(false); }}
          className={`relative w-14 h-14 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
            i === activeFrameIdx
              ? 'border-primary shadow-md shadow-primary/25'
              : 'border-transparent hover:border-primary/40'}`}
        >
          <img src={f} alt="" className="w-full h-full object-cover" draggable={false} />
          <span className="absolute bottom-0.5 right-0.5 px-1 rounded bg-dark/70 text-white text-[9px] font-bold leading-tight">{i + 1}</span>
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div className={`${shellClass} flex flex-col ${dockedLayout && embedded ? 'bg-white' : 'bg-surface'} select-none`}>
      {/* ── TOP BAR — matches /creatives:id (type + spacing). Hidden when
          embedded: the wizard already supplies a "Back to edit" summary bar
          above, so a second header would be redundant (and eat the fold). ── */}
      {!embedded && (
      <header className="flex-shrink-0 bg-white border-b border-surface-dark/40">
        <div className="max-w-[1452px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={handleClose} aria-label="Back"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-dark hover:bg-surface-dark/40 transition-colors cursor-pointer">
            <i className={`${backIcon} text-lg`} />
            <span className="hidden sm:inline text-sm font-medium">{backLabel}</span>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-dark truncate">{post.title || 'Untitled creative'}</h1>
            <p className="text-xs text-dark-muted">
              {new Date(post.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              {post.aspect_ratio ? ` · ${post.aspect_ratio}` : ''}
            </p>
          </div>
          {dirty && <span className="text-xs text-amber-600 font-bold flex-shrink-0">Unsaved</span>}
          {versions.length > 1 && (
            <div ref={versionsRef} className="relative flex-shrink-0">
              <button onClick={() => setVersionsOpen((o) => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface hover:bg-surface-dark/40 text-dark text-sm font-medium cursor-pointer transition-colors">
                <i className="ri-history-line" /> v{versions.findIndex((v) => v.id === post.id) + 1 || versions.length} of {versions.length}
                <i className="ri-arrow-down-s-line" />
              </button>
              {versionsOpen && (
                <div className="absolute right-0 z-50 mt-1 w-44 rounded-xl bg-white ring-1 ring-surface-dark/40 shadow-xl py-1">
                  {versions.map((v, i) => (
                    <button key={v.id}
                      onClick={() => { setVersionsOpen(false); if (v.id !== post.id) switchToPost(v.id); }}
                      className={`w-full text-left px-3 py-2 text-sm font-medium cursor-pointer hover:bg-surface ${v.id === post.id ? 'text-primary' : 'text-dark'}`}>
                      v{i + 1}{v.is_head ? ' · latest' : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {showDelete && (
            <button onClick={handleDelete} aria-label="Delete creative" title="Delete this creative"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-red-600 hover:bg-red-50 transition-colors text-sm font-medium cursor-pointer flex-shrink-0">
              <i className="ri-delete-bin-line text-lg" />
              <span className="hidden sm:inline">Delete image</span>
            </button>
          )}
        </div>
      </header>
      )}

      {/* ── BODY: left rail · canvas (+ floating toolbar) · right rail ──
          Wide (≥ FLOAT_MIN): asides become floating cards anchored to the
          artboard edges. Narrow: docked rails (unchanged). */}
      {dockedLayout ? (
        /* ── v5 · DOCKED 4-COLUMN LAYOUT (Product Shot, and the /creatives:id
            swap candidate) ─────────────────────────────────────────────────
            Width matches the picker (max-w-[1452px], centered). No selection →
            image centered, LAYERS + AI split the sides equally (same width).
            On select → PROPS opens LEFT of the image (image shifts right) and
            the AI panel shrinks to give it room. */
        <div className={`flex-1 min-h-0 flex ${embedded ? '' : 'justify-center items-start px-4 sm:px-6 py-6'}`}>
          {/* Route mode (/creatives) wraps the 4-column row in a centered,
              height-CAPPED max-width card so it isn't edge-to-edge (matches the
              wizard's bounded card). Embedded mode (wizard) already sits inside
              its own bounded card, so it just fills. */}
          <div ref={rowRef} className={`flex ${embedded
            ? 'min-h-0 w-full'
            : 'w-full max-w-[1500px] h-[calc(100vh-116px)] min-h-[520px] max-h-[880px] rounded-2xl bg-white overflow-hidden border border-surface-dark/40 shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)]'}`}>
            {/* LEFT — LAYERS. Flex-fills the free space (equal to the AI panel →
                image centered). Shrinks to min-w when PROPS opens; never eats
                into the image (the image is a fixed-width flex-shrink-0 main). */}
            <aside className="hidden md:flex flex-col flex-1 min-w-[200px] p-3 bg-white border-r border-surface-dark/40 overflow-y-auto no-scrollbar">
              {layersPanel}
            </aside>
            {/* PROPS — slides open left of the image when a layer is selected.
                Always mounted; the aside animates its width 0 → w-64 while the
                inner content keeps a fixed width so it doesn't reflow. */}
            <aside className={`hidden md:flex flex-shrink-0 overflow-hidden transition-[width] duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${selected ? 'w-64 border-r border-surface-dark/40' : 'w-0'}`}>
              <div className="w-64 flex-shrink-0 flex flex-col p-3 h-full overflow-y-auto no-scrollbar">
                {propsPanel}
              </div>
            </aside>
            {/* CENTER — image. FIXED to its height-driven width (flex-shrink-0)
                so it's always the max size the card allows and never shrinks for
                the panels. The +gutter gives even breathing room all around. */}
            <main ref={stageRef}
              className="relative flex-shrink-0 flex flex-col items-center justify-center bg-white transition-[width] duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
              style={boxW > 0 ? { width: `${boxW + DOCK_GUTTER * 2}px` } : undefined}
              onMouseDown={() => setActiveId(null)}>
              {artboard}
              {frameThumbs}
            </main>
            {/* RIGHT — Edit with AI (vertical) + Save. Flex-fills the free space;
                shrinks to min-w as PROPS opens (never touches the image). */}
            <aside className="hidden md:flex flex-col flex-1 min-w-[200px] p-3 bg-white border-l border-surface-dark/40 overflow-y-auto no-scrollbar">
              <EditAiPanelVertical
                aiPrompt={aiPrompt} setAiPrompt={setAiPrompt} onAiEdit={handleAiEdit} aiBusy={aiBusy}
                marqueeMode={marqueeMode} onToggleMarquee={() => setMarqueeMode((v) => !v)}
                retryQ={retryQ} onReRender={handleReRender} onEditBakedText={handleEditBakedText}
                hasBakedText={hasBakedText}
                mutating={mutating} opError={opError}
                onSave={handleSave} saving={saving} dirty={dirty} />
            </aside>
          </div>
        </div>
      ) : (
        /* ── FLOAT / narrow-docked layout (/creatives) — unchanged ────────── */
        <div className="flex-1 min-h-0 relative flex">
          <aside
            className={floatMode
              ? 'hidden md:block absolute z-30 w-60 rounded-2xl bg-white ring-1 ring-surface-dark/50 shadow-[0_20px_50px_-15px_rgba(15,15,30,0.25)] overflow-hidden'
              : 'hidden md:block w-60 flex-shrink-0 p-3 bg-white border-r border-surface-dark/40 overflow-y-auto no-scrollbar'}
            style={floatMode ? { right: `calc(50% + ${boxW / 2 + PANEL_GAP}px)`, top: `${FLOAT_TOP}px`, height: `${boxH}px` } : undefined}>
            {floatMode ? <div className="h-full bg-white p-3 overflow-y-auto no-scrollbar">{layersPanel}</div> : layersPanel}
          </aside>

          <main ref={stageRef}
            className={`relative flex-1 min-w-0 flex justify-center bg-surface-dark/30 ${floatMode ? 'items-start px-4 sm:px-8 pt-6' : 'items-center p-4 sm:p-8'}`}
            onMouseDown={() => setActiveId(null)}>
            {frameThumbs ? (
              <div className="flex flex-col items-center">{artboard}{frameThumbs}</div>
            ) : artboard}
            <FloatingToolbar
              aiPrompt={aiPrompt} setAiPrompt={setAiPrompt} onAiEdit={handleAiEdit} aiBusy={aiBusy}
              marqueeMode={marqueeMode} onToggleMarquee={() => setMarqueeMode((v) => !v)}
              retryQ={retryQ} onReRender={handleReRender} mutating={mutating} opError={opError}
              topPx={floatMode && boxH > 0 ? FLOAT_TOP + boxH + PANEL_GAP : undefined}
              widthPx={floatMode && boxW > 0 ? Math.min(1160, Math.max(boxW, 940)) : undefined}
            />
          </main>

          <aside
            className={floatMode
              ? 'hidden md:block absolute z-30 w-72 rounded-2xl bg-white ring-1 ring-surface-dark/50 shadow-[0_20px_50px_-15px_rgba(15,15,30,0.25)] overflow-hidden'
              : 'hidden md:block w-72 flex-shrink-0 p-3 bg-white border-l border-surface-dark/40 overflow-y-auto no-scrollbar'}
            style={floatMode ? { left: `calc(50% + ${boxW / 2 + PANEL_GAP}px)`, top: `${FLOAT_TOP}px`, height: `${boxH}px` } : undefined}>
            {floatMode ? <div className="h-full bg-white p-3 overflow-y-auto no-scrollbar">{propsPanel}</div> : propsPanel}
          </aside>
        </div>
      )}

      {/* ── MOBILE bottom sheet (Layers | Properties) ── */}
      <div className="md:hidden flex-shrink-0 border-t border-surface-dark/40 bg-white max-h-[42vh] flex flex-col">
        <div className="flex p-1 gap-1">
          {(['layers', 'props'] as const).map((t) => (
            <button key={t} onClick={() => setMobileTab(t)}
              className={`flex-1 h-9 rounded-lg text-xs font-bold uppercase tracking-wide cursor-pointer ${mobileTab === t ? 'bg-primary text-white' : 'text-dark-muted'}`}>
              {t === 'layers' ? 'Layers' : 'Properties'}
            </button>
          ))}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar p-3">
          {mobileTab === 'layers' ? layersPanel : propsPanel}
        </div>
      </div>

      {/* Publish — hidden for V1 (SOCIAL_PUBLISHING_ENABLED) */}
      {SOCIAL_PUBLISHING_ENABLED && publishOpen && (
        <PostToSocialModal
          post={post}
          initialEnabled={['facebook', 'instagram']}
          headerLabel="Post or schedule"
          onClose={() => setPublishOpen(false)}
          onSuccess={(channels, mode) => {
            const names = channels.map((c) => c === 'instagram' ? 'Instagram' : c === 'facebook' ? 'Facebook' : c === 'tiktok' ? 'TikTok' : 'X').join(' + ');
            setToast(`${mode === 'now' ? 'Posted to' : 'Scheduled for'} ${names}`);
            setPublishOpen(false);
          }}
        />
      )}

      {/* Fullscreen preview */}
      {fullscreenUrl && (
        <div className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out" onClick={closeFullscreen}>
          <img src={fullscreenUrl} alt="Full screen preview" className="max-w-[96vw] max-h-[96vh] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <button onClick={closeFullscreen} title="Close" className="absolute top-4 right-4 h-10 w-10 inline-flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] px-4 py-2.5 rounded-full bg-dark text-white text-sm font-bold shadow-xl"
          onAnimationEnd={() => setToast(null)}>
          <button onClick={() => setToast(null)} className="cursor-pointer">{toast}</button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Floating toolbar — AI edit + Marquee + Re-render, docked bottom-center over
// the canvas. Stationary (doesn't scroll with the property rail).
// ═══════════════════════════════════════════════════════════════════════════
function FloatingToolbar(props: {
  aiPrompt: string; setAiPrompt: (v: string) => void; onAiEdit: () => void; aiBusy: boolean;
  marqueeMode: boolean; onToggleMarquee: () => void;
  retryQ: 'low' | 'medium' | 'high' | null; onReRender: (q: 'low' | 'medium' | 'high') => void;
  mutating: boolean; opError: string | null;
  /** Float mode: pixel top (hug the artboard bottom) + matched width. */
  topPx?: number; widthPx?: number;
  /** Hidden for carousels (region-edit can't target a specific frame). */
  hideMarquee?: boolean;
}) {
  const { aiPrompt, setAiPrompt, onAiEdit, aiBusy, marqueeMode, onToggleMarquee, retryQ, onReRender, mutating, opError, topPx, widthPx, hideMarquee } = props;
  const [rrOpen, setRrOpen] = useState(false);
  const rrRef = useRef<HTMLDivElement>(null);
  // Close the Re-render menu on any click outside it. Capture phase so the
  // canvas/overlays' stopPropagation() on mousedown can't swallow the close.
  useEffect(() => {
    if (!rrOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!rrRef.current?.contains(e.target as Node)) setRrOpen(false);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [rrOpen]);
  const floating = topPx != null;
  return (
    <div
      className={`absolute left-1/2 -translate-x-1/2 z-40 pointer-events-none ${floating ? '' : 'bottom-5 sm:bottom-8 w-[min(1040px,96%)]'}`}
      style={floating ? { top: `${topPx}px`, width: `${widthPx}px` } : undefined}>
      <div className="pointer-events-auto rounded-[24px] bg-white ring-1 ring-surface-dark/50 shadow-[0_24px_60px_-16px_rgba(15,15,30,0.4)]">
        <div className="rounded-[24px] p-2.5 flex items-center gap-2.5">
          <span className="hidden lg:inline-flex items-center gap-1.5 pl-2 pr-1 text-sm font-bold text-dark whitespace-nowrap">
            <i className="ri-magic-line text-primary text-lg" /> Edit Image
          </span>
          <div className="flex-1 flex items-center gap-2 h-12 pl-4 pr-2 rounded-full bg-white ring-[1.5px] ring-primary/45 focus-within:ring-2 focus-within:ring-primary/70 transition-shadow">
            <i className="ri-sparkling-2-fill text-primary text-lg animate-pulse" />
            <input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} disabled={mutating} autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && aiPrompt.trim()) { e.preventDefault(); onAiEdit(); } }}
              placeholder="Describe an edit — e.g. make the background a sunset"
              className="flex-1 min-w-0 h-12 bg-transparent text-[15px] text-dark caret-primary focus:outline-none placeholder:text-dark-muted/80 disabled:opacity-50" />
          </div>
          <button onClick={onAiEdit} disabled={mutating || !aiPrompt.trim()}
            className="h-12 px-5 inline-flex items-center gap-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shadow-md shadow-primary/25">
            {aiBusy ? <><i className="ri-loader-4-line animate-spin" /> Editing…</> : <><i className="ri-magic-line" /> Apply · 1 cr</>}
          </button>
          <div className="w-px h-8 bg-surface-dark/40" />
          {!hideMarquee && (
          <button onClick={onToggleMarquee} disabled={mutating} title="Edit a region"
            className={`h-12 px-4 inline-flex items-center gap-1.5 rounded-full text-sm font-bold cursor-pointer disabled:opacity-40 whitespace-nowrap ${marqueeMode ? 'bg-primary-light text-primary' : 'bg-surface text-dark hover:bg-surface-dark/30'}`}>
            <i className="ri-crop-line" /> Edit Region
          </button>
          )}
          <div ref={rrRef} className="relative">
            <button onClick={() => setRrOpen((o) => !o)} disabled={mutating} title="Re-render at a quality"
              className="h-12 px-4 inline-flex items-center gap-1.5 rounded-full bg-surface text-dark hover:bg-surface-dark/30 text-sm font-bold cursor-pointer disabled:opacity-40 whitespace-nowrap">
              {retryQ ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-refresh-line" />}
              Re-render <i className="ri-arrow-up-s-line" />
            </button>
            {rrOpen && !mutating && (
              <div className="absolute bottom-full right-0 mb-2 w-40 rounded-xl bg-white ring-1 ring-surface-dark/40 shadow-xl p-1">
                {(['low', 'medium', 'high'] as const).map((q) => (
                  <button key={q} onClick={() => { setRrOpen(false); onReRender(q); }}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs font-bold uppercase text-dark hover:bg-surface cursor-pointer">
                    {q} · {q === 'low' ? '1' : q === 'medium' ? '2' : '4'} cr
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {opError && <p className="pointer-events-auto mt-2 text-center text-xs text-red-600 bg-white/90 rounded-lg py-1 px-2 mx-auto w-max shadow-sm">{opError}</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EditAiPanelVertical — the DOCKED (embedded / Product Shot) variant of the
// AI toolbar: a vertical right rail with the prompt box, Apply, Marquee,
// Re-render, and Save stacked. Same handlers as FloatingToolbar + onSave.
// ═══════════════════════════════════════════════════════════════════════════
function EditAiPanelVertical(props: {
  aiPrompt: string; setAiPrompt: (v: string) => void; onAiEdit: () => void; aiBusy: boolean;
  marqueeMode: boolean; onToggleMarquee: () => void;
  retryQ: 'low' | 'medium' | 'high' | null; onReRender: (q: 'low' | 'medium' | 'high') => void;
  onEditBakedText: () => void;
  /** false → the image has no baked-in copy to edit; disable the tool. */
  hasBakedText: boolean;
  mutating: boolean; opError: string | null;
  onSave: () => void; saving: boolean; dirty: boolean;
  /** Region-edit (marquee) hits a post-level endpoint with no frame index, so
   *  it can't target a specific carousel frame — hidden for carousels. */
  hideMarquee?: boolean;
}) {
  const { aiPrompt, setAiPrompt, onAiEdit, aiBusy, marqueeMode, onToggleMarquee, retryQ, onReRender, onEditBakedText, hasBakedText, mutating, opError, onSave, saving, dirty, hideMarquee } = props;
  const [rrOpen, setRrOpen] = useState(false);
  const rrRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!rrOpen) return;
    const onDown = (e: MouseEvent) => { if (!rrRef.current?.contains(e.target as Node)) setRrOpen(false); };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [rrOpen]);
  const btn = 'w-full h-11 px-3 inline-flex items-center justify-center gap-2 rounded-full text-sm font-bold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Edit with AI — the hero action, elevated in a soft tinted card */}
      <div className="rounded-2xl bg-primary-light/40 ring-1 ring-primary/15 p-3 flex flex-col gap-2.5 shadow-sm shadow-primary/5">
        <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] text-primary"><i className="ri-magic-line text-base" /> Edit Image</p>
        <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} disabled={mutating}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && aiPrompt.trim()) { e.preventDefault(); onAiEdit(); } }}
          placeholder="Describe an edit — e.g. make the background a sunset"
          className="w-full h-56 resize-none rounded-xl bg-white ring-[1.5px] ring-primary/45 focus:ring-2 focus:ring-primary/70 px-3.5 py-3 text-sm text-dark caret-primary focus:outline-none placeholder:text-dark-muted/80 disabled:opacity-50 transition-shadow" />
        <button onClick={onAiEdit} disabled={mutating || !aiPrompt.trim()}
          className={`${btn} bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25`}>
          {aiBusy ? <><i className="ri-loader-4-line animate-spin" /> Editing…</> : <><i className="ri-magic-line" /> Apply · 1 cr</>}
        </button>
        {opError && <p className="text-xs text-red-600 bg-red-50 rounded-lg py-1.5 px-2">{opError}</p>}
      </div>

      {/* Tools — region edit, baked-text edit, and re-render grouped together */}
      <div className="mt-4 pt-4 border-t border-surface-dark/40 flex flex-col gap-2">
        <p className={sectionLabel}>Tools</p>
        {!hideMarquee && (
          <button onClick={onToggleMarquee} disabled={mutating}
            className={`${btn} ${marqueeMode ? 'bg-primary-light text-primary' : 'bg-surface text-dark hover:bg-surface-dark/30'}`}>
            <i className="ri-crop-line" /> Edit Region
          </button>
        )}
        {hasBakedText && (
          <button onClick={onEditBakedText} disabled={mutating}
            title="Lift the baked-in headline/subhead into editable text layers"
            className={`${btn} bg-surface text-dark hover:bg-surface-dark/30`}>
            <i className="ri-text" /> Edit baked text · 2cr
          </button>
        )}
        <div ref={rrRef} className="relative">
          <button onClick={() => setRrOpen((o) => !o)} disabled={mutating}
            className={`${btn} bg-surface text-dark hover:bg-surface-dark/30`}>
            {retryQ ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-refresh-line" />} Re-render <i className="ri-arrow-down-s-line" />
          </button>
          {rrOpen && !mutating && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-xl bg-white ring-1 ring-surface-dark/40 shadow-xl p-1">
              {(['low', 'medium', 'high'] as const).map((q) => (
                <button key={q} onClick={() => { setRrOpen(false); onReRender(q); }}
                  className="w-full text-left px-3 py-2 rounded-lg text-xs font-bold uppercase text-dark hover:bg-surface cursor-pointer">
                  {q} · {q === 'low' ? '1' : q === 'medium' ? '2' : '4'} cr
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-auto pt-3 border-t border-surface-dark/40">
        <button onClick={onSave} disabled={saving || mutating || !dirty}
          className={`${btn} bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25`}>
          {saving ? <><i className="ri-loader-4-line animate-spin" /> Saving…</> : <><i className="ri-save-3-line" /> Save</>}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Layers rail
// ═══════════════════════════════════════════════════════════════════════════
function LayersPanel(props: {
  logoLayer: LogoOverlay | null; ctaLayer: TextOverlay | null; textLayers: TextOverlay[];
  activeId: string | null; setActiveId: (id: string | null) => void;
  addLogoLayer: () => void; addCtaLayer: () => void; addTextLayer: () => void;
  removeLayer: (id: string) => void; brandLogoUrl: string | null; mutating: boolean;
  onEditBakedText: () => void;
  /** false → the image has no baked-in copy to edit; disable the tool. */
  hasBakedText: boolean;
  onPost: () => void; onDownload: () => void; downloading: boolean;
  onSave: () => void; saving: boolean; dirty: boolean;
  /** Docked (embedded) mode moves Save into the AI panel — hide it here. */
  hideSave?: boolean;
}) {
  const { logoLayer, ctaLayer, textLayers, activeId, setActiveId, addLogoLayer, addCtaLayer, addTextLayer, removeLayer, brandLogoUrl, mutating, onEditBakedText, hasBakedText, onPost, onDownload, downloading, onSave, saving, dirty, hideSave } = props;
  // Account-aware: FB/IG "Post" is hidden for real users, on for reviewer/team.
  const SOCIAL_PUBLISHING_ENABLED = useSocialPublishingEnabled();
  const Row = ({ id, label, icon }: { id: string; label: string; icon: string }) => (
    <div className={`flex items-center gap-2 h-10 px-3 rounded-lg cursor-pointer group ${activeId === id ? 'bg-primary-light ring-1 ring-primary/50' : 'bg-surface hover:bg-surface-dark/30'}`}
      onClick={() => setActiveId(id)}>
      <i className={`${icon} ${activeId === id ? 'text-primary' : 'text-dark-muted'}`} />
      <span className={`flex-1 min-w-0 truncate text-xs font-bold uppercase tracking-wide ${activeId === id ? 'text-primary' : 'text-dark'}`}>{label}</span>
      <button onClick={(e) => { e.stopPropagation(); removeLayer(id); }} className="opacity-0 group-hover:opacity-100 text-dark-muted hover:text-red-500 cursor-pointer">
        <i className="ri-delete-bin-line" />
      </button>
    </div>
  );
  const fileBtn = 'w-full h-10 px-3 inline-flex items-center gap-2 rounded-lg bg-primary-light text-primary border border-primary/40 hover:bg-primary hover:text-white text-xs font-bold uppercase tracking-wide cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex flex-col gap-3">
        <p className={sectionLabel}>Layers</p>
        {logoLayer ? <Row id={logoLayer.id} label="Logo" icon="ri-image-line" /> : (
          <button className={railBtn} onClick={addLogoLayer} disabled={mutating || !brandLogoUrl} title={brandLogoUrl ? '' : 'Set a brand logo first'}><span className="inline-flex items-center justify-center w-5 h-5 rounded-full border-[1.5px] border-primary text-primary text-[13px] leading-none"><i className="ri-add-line" /></span>Logo</button>
        )}
        {ctaLayer ? <Row id={ctaLayer.id} label={ctaLayer.text || 'CTA'} icon="ri-cursor-line" /> : (
          <button className={railBtn} onClick={addCtaLayer} disabled={mutating}><span className="inline-flex items-center justify-center w-5 h-5 rounded-full border-[1.5px] border-primary text-primary text-[13px] leading-none"><i className="ri-add-line" /></span>CTA</button>
        )}
        {textLayers.map((t) => <Row key={t.id} id={t.id} label={t.text || 'Text'} icon="ri-text" />)}
        <button className={railBtn} onClick={addTextLayer} disabled={mutating}><span className="inline-flex items-center justify-center w-5 h-5 rounded-full border-[1.5px] border-primary text-primary text-[13px] leading-none"><i className="ri-add-line" /></span>Text</button>
      </div>

      {/* File actions — bottom-docked, divider on top. In the docked/embedded
          editor Post + Download share one row (2-col); otherwise they stack. */}
      <div className="mt-auto pt-3 border-t border-surface-dark/40 flex flex-col gap-2">
        {hideSave ? (
          /* Docked: Post is an icon (compact) so Download's label never clips. */
          <div className="flex gap-2">
            {/* Post → social publish. Hidden for V1 (SOCIAL_PUBLISHING_ENABLED). */}
            {SOCIAL_PUBLISHING_ENABLED && (
            <button onClick={onPost} disabled={mutating} title="Post" aria-label="Post"
              className="h-10 w-11 flex-shrink-0 inline-flex items-center justify-center rounded-lg bg-primary-light text-primary border border-primary/40 hover:bg-primary hover:text-white cursor-pointer transition-colors disabled:opacity-40">
              <i className="ri-send-plane-line text-base" />
            </button>
            )}
            <button onClick={onDownload} disabled={downloading || mutating} className={`${fileBtn} flex-1 min-w-0`}>
              {downloading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-download-2-line" />} Download
            </button>
          </div>
        ) : (
          <>
            {SOCIAL_PUBLISHING_ENABLED && <button onClick={onPost} disabled={mutating} className={fileBtn}><i className="ri-send-plane-line" /> Post</button>}
            <button onClick={onDownload} disabled={downloading || mutating} className={fileBtn}>
              {downloading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-download-2-line" />} Download
            </button>
          </>
        )}
        {!hideSave && (
          <button onClick={onSave} disabled={saving || mutating || !dirty}
            className="w-full h-10 px-3 inline-flex items-center gap-2 rounded-lg bg-primary text-white hover:bg-primary-hover text-xs font-bold uppercase tracking-wide cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-3-line" />} Save
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Property panel — the selected layer's properties (scrolls independently)
// ═══════════════════════════════════════════════════════════════════════════
function PropertyPanel(props: {
  activeOverlay: TextOverlay | null; activeLogo: LogoOverlay | null; brandColors: string[];
  updateOverlay: (id: string, u: Partial<TextOverlay>) => void;
  updateLogo: (id: string, u: Partial<LogoOverlay>) => void;
  removeLayer: (id: string) => void;
  placeTextAt: (posId: string) => void; placeLogoAt: (posId: string) => void;
  hasAiText: boolean; onAiFill: (id: string) => void; aiTextBusy: boolean;
}) {
  const { activeOverlay: o, activeLogo: lg, brandColors, updateOverlay, updateLogo, removeLayer, placeTextAt, placeLogoAt, hasAiText, onAiFill, aiTextBusy } = props;

  if (!o && !lg) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
        <i className="ri-cursor-line text-2xl text-dark-muted/40" />
        <p className="text-xs text-dark-muted max-w-[180px]">Select a layer — or add one — then drag it on the canvas.</p>
      </div>
    );
  }

  const Grid = ({ onPick }: { onPick: (p: string) => void }) => (
    <div className="grid grid-cols-3 gap-1 w-max">
      {PLACEMENT_CELLS.map((c) => (
        <button key={c} onClick={() => onPick(c)} title={c}
          className="w-7 h-7 rounded bg-surface hover:bg-primary flex items-center justify-center cursor-pointer group">
          <span className="w-1.5 h-1.5 rounded-full bg-dark-muted group-hover:bg-white" />
        </button>
      ))}
    </div>
  );
  const deleteBtn = (id: string) => (
    <button onClick={() => removeLayer(id)} className="mt-2 h-9 rounded-lg bg-red-50 text-red-600 text-xs font-bold uppercase cursor-pointer hover:bg-red-100"><i className="ri-delete-bin-line mr-1" /> Delete layer</button>
  );

  if (lg) {
    return (
      <div className="flex flex-col gap-3">
        <p className={sectionLabel}>Logo</p>
        <label className="text-[11px] text-dark-muted">Size — {Math.round(lg.widthFrac * 100)}%</label>
        <input type="range" min={5} max={50} value={Math.round(lg.widthFrac * 100)} onChange={(e) => updateLogo(lg.id, { widthFrac: Number(e.target.value) / 100 })} className="w-full accent-primary" />
        <label className="text-[11px] text-dark-muted">Opacity — {Math.round(lg.opacity * 100)}%</label>
        <input type="range" min={20} max={100} value={Math.round(lg.opacity * 100)} onChange={(e) => updateLogo(lg.id, { opacity: Number(e.target.value) / 100 })} className="w-full accent-primary" />
        <p className={sectionLabel}>Tint</p>
        <div className="flex gap-2">{(['original', 'black', 'white'] as LogoTint[]).map((t) => <button key={t} onClick={() => updateLogo(lg.id, { tint: t })} className={chip(lg.tint === t)}>{t}</button>)}</div>
        <p className={sectionLabel}>Placement</p>
        <Grid onPick={placeLogoAt} />
        {deleteBtn(lg.id)}
      </div>
    );
  }

  if (!o) return null;
  const isCta = o.id.startsWith('cta-');
  return (
    <div className="flex flex-col gap-3">
      {/* Text content — with an AI-text button (cycles the render's copy) */}
      <div className="flex items-center justify-between">
        <p className={sectionLabel}>{isCta ? 'CTA' : 'Text'}</p>
        {!isCta && hasAiText && (
          <button onClick={() => onAiFill(o.id)} disabled={aiTextBusy} title="Insert fresh AI-written copy"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-light text-primary text-[11px] font-bold cursor-pointer hover:bg-primary hover:text-white transition-colors disabled:opacity-60 disabled:cursor-wait">
            <i className={aiTextBusy ? 'ri-loader-4-line animate-spin' : 'ri-magic-line'} /> AI text
          </button>
        )}
      </div>
      <textarea value={o.text} onChange={(e) => updateOverlay(o.id, { text: e.target.value })} rows={2}
        className="w-full px-3 py-2 rounded-lg bg-white border border-dark-muted/30 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none text-sm text-dark resize-none transition-shadow" />

      <p className={sectionLabel}>Font</p>
      <select value={o.fontFamily} onChange={(e) => updateOverlay(o.id, { fontFamily: e.target.value })}
        className="w-full h-9 px-2 rounded-lg bg-white border border-dark-muted/30 text-dark text-sm focus:border-primary focus:outline-none">
        {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>

      <label className="text-[11px] text-dark-muted">Size — {o.fontSize}</label>
      <input type="range" min={20} max={160} value={o.fontSize} onChange={(e) => updateOverlay(o.id, { fontSize: Number(e.target.value) })} className="w-full accent-primary" />

      {/* Weight + Case + Align — each its own labeled row (no longer mixed) */}
      <div>
        <p className={`${sectionLabel} mb-1.5`}>Weight</p>
        <div className="flex gap-2">{WEIGHT_OPTIONS.map((w) => <button key={w.l} onClick={() => updateOverlay(o.id, { fontWeight: w.v })} className={chip(o.fontWeight === w.v)}>{w.l}</button>)}</div>
      </div>
      <div>
        <p className={`${sectionLabel} mb-1.5`}>Case</p>
        <div className="flex gap-2">{CASE_OPTIONS.map((c) => <button key={c.l} onClick={() => updateOverlay(o.id, { textTransform: c.v })} className={chip(o.textTransform === c.v)}>{c.l}</button>)}</div>
      </div>
      <div>
        <p className={`${sectionLabel} mb-1.5`}>Align</p>
        <div className="flex gap-2">{ALIGN_OPTIONS.map((a) => <button key={a.v} onClick={() => updateOverlay(o.id, { textAlign: a.v })} className={chip(o.textAlign === a.v)}><i className={a.icon} /></button>)}</div>
      </div>

      <p className={sectionLabel}>Color</p>
      <div className="flex items-center gap-2 flex-wrap">
        {[...BW_SWATCHES, ...brandColors].map((c) => (
          <button key={c} onClick={() => updateOverlay(o.id, { color: c })} title={c}
            className={`w-7 h-7 rounded-full border-2 cursor-pointer ${o.color?.toLowerCase() === c.toLowerCase() ? 'border-primary' : 'border-surface-dark/40'}`}
            style={{ backgroundColor: c }} />
        ))}
        <input type="color" value={o.color || '#ffffff'} onChange={(e) => updateOverlay(o.id, { color: e.target.value })} className="w-7 h-7 rounded cursor-pointer bg-transparent border border-surface-dark/40" />
      </div>

      <p className={sectionLabel}>Placement</p>
      <Grid onPick={placeTextAt} />
      {deleteBtn(o.id)}
    </div>
  );
}
